import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Checkbox,
  Stack,
  useOutsideClick,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import api from '../api'; // Centralized API instance for requests

const CategoryFilter = ({ selectedCategories = [], onCategoryChange, onClose }) => {
  const [categories, setCategories] = useState([]);
  const dropdownRef = useRef();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/hierarchy/');
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  useOutsideClick({
    ref: dropdownRef,
    handler: onClose,
  });

  const isCategorySelected = (categoryId) =>
    selectedCategories.some((cat) => cat.id === categoryId);

  const determineSelectionState = (category) => {
    if (!category.children || category.children.length === 0) {
      return {
        isSelected: isCategorySelected(category.id),
        isIndeterminate: false,
      };
    }

    const childStates = category.children.map(determineSelectionState);
    const allSelected = childStates.every((state) => state.isSelected);
    const someSelected = childStates.some((state) => state.isSelected || state.isIndeterminate);

    return {
      isSelected: allSelected,
      isIndeterminate: someSelected && !allSelected,
    };
  };

  const handleCategorySelection = (category, isSelected) => {
    const updateSelection = (cat, selected, updatedCategories) => {
      if (selected) {
        // Select the current category
        if (!updatedCategories.some((item) => item.id === cat.id)) {
          updatedCategories.push({ id: cat.id, name: cat.name });
        }
      } else {
        // Unselect the current category
        updatedCategories = updatedCategories.filter((item) => item.id !== cat.id);
      }

      // Recursively select/unselect child categories
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach((child) => {
          updatedCategories = updateSelection(child, selected, updatedCategories);
        });
      }

      return updatedCategories;
    };

    const updateParentSelection = (category, allCategories, updatedCategories) => {
      const findParent = (childCategory, categories) => {
        for (const parent of categories) {
          if (parent.children && parent.children.some((child) => child.id === childCategory.id)) {
            return parent;
          }
          if (parent.children) {
            const foundParent = findParent(childCategory, parent.children);
            if (foundParent) return foundParent;
          }
        }
        return null;
      };

      const parentCategory = findParent(category, allCategories);
      if (!parentCategory) return updatedCategories;

      const childStates = parentCategory.children.map((child) =>
        updatedCategories.some((item) => item.id === child.id)
      );

      const allChildrenSelected = childStates.every(Boolean);
      const someChildrenSelected = childStates.some(Boolean);

      if (allChildrenSelected) {
        if (!updatedCategories.some((item) => item.id === parentCategory.id)) {
          updatedCategories.push({ id: parentCategory.id, name: parentCategory.name });
        }
      } else {
        updatedCategories = updatedCategories.filter((item) => item.id !== parentCategory.id);
      }

      return updateParentSelection(parentCategory, allCategories, updatedCategories);
    };

    let updatedCategories = [...selectedCategories];

    // Update the current category (and its children)
    updatedCategories = updateSelection(category, isSelected, updatedCategories);

    // Update the parent state (to set the indeterminate state properly)
    updatedCategories = updateParentSelection(category, categories, updatedCategories);

    // Update the state via the onCategoryChange prop
    onCategoryChange(updatedCategories);
  };

  const renderCategories = (categoryList, parentPath = []) =>
    categoryList.map((category) => {
      const currentPath = [...parentPath, category.name];
      const { isSelected, isIndeterminate } = determineSelectionState(category);

      return (
        <AccordionItem key={category.id} border="none">
          <AccordionButton
            _hover={{ bg: 'gray.100' }}
            pl={parentPath.length * 4}
            py="2"
          >
            <Checkbox
              isChecked={isSelected}
              isIndeterminate={isIndeterminate}
              onChange={() => handleCategorySelection(category, !isSelected)}
              mr="4"
            />
            <Box flex="1" textAlign="left">
              {category.name}
            </Box>
            {category.children && category.children.length > 0 && <ChevronDownIcon />}
          </AccordionButton>
          {category.children && category.children.length > 0 && (
            <AccordionPanel pl={parentPath.length * 4 + 8}>
              <Stack spacing="2">{renderCategories(category.children, currentPath)}</Stack>
            </AccordionPanel>
          )}
        </AccordionItem>
      );
    });

  return (
    <Box
      ref={dropdownRef}
      maxH="300px"
      overflowY="auto"
      border="1px solid #e2e8f0"
      borderRadius="md"
      p="4"
      w="320px"
    >
      <Accordion allowMultiple>{renderCategories(categories)}</Accordion>
    </Box>
  );
};

export default CategoryFilter;
